import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Truck } from 'lucide-react';

// Shared test console for Phase 10-T (Payment) + 11-T (Delivery).
// Renders NOTHING when the caller isn't authorised for /seller/orders/{id}/history
// (backend re-authorises every mutation). Safe to render for guests — they'll
// silently receive 403 on refresh() and see nothing.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  const s = localStorage.getItem('active_seller_id');
  const h = {};
  if (t) h['Authorization'] = `Bearer ${t}`;
  if (s) h['X-Seller-Id'] = s;
  return h;
};

const STATUS_LABEL = {
  pending: '결제대기', paid: '결제완료', preparing: '배송준비',
  shipping: '배송중', delivered: '배송완료', completed: '주문완료',
  cancelled: '취소', refunded: '환불', failed: '실패',
};
const DELIVERY_LABEL = {
  pending: '배송대기', invoice_created: '배송준비', pickup_done: '집화완료',
  in_transit: '배송중', delivered: '배송완료',
};

export default function OrderTestConsole({ orderId, compact = false, onChanged }) {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [visible, setVisible] = useState(true);

  const refresh = useCallback(async () => {
    if (!orderId) return;
    try {
      const r = await axios.get(`${API}/seller/order-timeline/${orderId}`, { headers: authHeaders() });
      setOrder(r.data.order);
      setHistory(r.data.history || []);
      setVisible(true);
    } catch {
      setVisible(false); // not authorised → hide the whole panel
    }
  }, [orderId]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [refresh]);

  const call = async (path, body) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}${path}`, body || {}, { headers: authHeaders() });
      toast.success(`✔ ${r.data.event || 'ok'}`);
      await refresh();
      onChanged?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '실패');
    } finally {
      setBusy(false);
    }
  };

  if (!visible || !order) return null;

  const pay = order.payment_status || 'pending';
  const del = order.delivery_status || 'pending';
  const dot = 'px-2 py-1 rounded-full text-[10px] font-bold';

  return (
    <div
      className={`rounded-2xl border border-orange-500/30 bg-orange-500/5 ${compact ? 'p-2' : 'p-3'} space-y-2`}
      data-testid={`test-console-${orderId}`}
    >
      <p className="text-[10px] font-black text-orange-700 dark:text-orange-400 inline-flex items-center gap-1">
        🧪 개발자 테스트 · 실 결제/실 배송 발생하지 않음
      </p>

      <div className="text-[10px] leading-snug space-y-0.5">
        <p>
          주문상태 <b>{STATUS_LABEL[order.status] || order.status}</b>
          {' · '}결제 <b data-testid={`tc-payment-status-${orderId}`}>{STATUS_LABEL[pay] || pay}</b>
          {del !== 'pending' && (
            <>{' · '}배송 <b data-testid={`tc-delivery-status-${orderId}`}>{DELIVERY_LABEL[del] || del}</b></>
          )}
        </p>
        {order.tracking_number && (
          <p className="text-gray-500">
            송장 <span className="font-mono" data-testid={`tc-tracking-${orderId}`}>{order.tracking_number}</span>
            {' '}({order.delivery_carrier_name})
          </p>
        )}
      </div>

      <div>
        <p className="text-[10px] font-bold text-gray-500 mb-1 inline-flex items-center gap-1">
          <CreditCard size={10} /> 가상 결제
        </p>
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => call(`/seller/orders/${orderId}/test-payment`, { action: 'success', method: 'card' })}
            disabled={busy || pay === 'paid'}
            className={`${dot} bg-emerald-500 text-white disabled:opacity-30`}
            data-testid={`tc-pay-success-${orderId}`}
          >
            결제 성공
          </button>
          <button
            onClick={() => call(`/seller/orders/${orderId}/test-payment`, { action: 'failure', reason: 'card_declined' })}
            disabled={busy}
            className={`${dot} bg-red-500 text-white disabled:opacity-30`}
            data-testid={`tc-pay-fail-${orderId}`}
          >
            결제 실패
          </button>
          <button
            onClick={() => call(`/seller/orders/${orderId}/test-payment`, { action: 'cancel', reason: 'test cancel' })}
            disabled={busy || pay !== 'paid'}
            className={`${dot} border border-orange-500 text-orange-600 disabled:opacity-30`}
            data-testid={`tc-pay-cancel-${orderId}`}
          >
            결제 취소
          </button>
          <button
            onClick={() => call(`/seller/orders/${orderId}/test-payment`, { action: 'refund', reason: 'test refund' })}
            disabled={busy || pay !== 'paid'}
            className={`${dot} border border-orange-500 text-orange-600 disabled:opacity-30`}
            data-testid={`tc-pay-refund-${orderId}`}
          >
            환불
          </button>
          <button
            onClick={() => call(`/seller/orders/${orderId}/test-payment`, { action: 'virtual_account_paid' })}
            disabled={busy || pay === 'paid'}
            className={`${dot} bg-blue-500 text-white disabled:opacity-30`}
            data-testid={`tc-pay-va-${orderId}`}
          >
            가상 계좌입금 완료
          </button>
        </div>
      </div>

      {pay === 'paid' && (
        <div>
          <p className="text-[10px] font-bold text-gray-500 mb-1 inline-flex items-center gap-1">
            <Truck size={10} /> 가상 배송
          </p>
          <div className="flex gap-1 flex-wrap">
            {!order.tracking_number && (
              <button
                onClick={() => call(`/seller/orders/${orderId}/test-delivery`, { action: 'create_invoice', carrier: 'cj' })}
                disabled={busy}
                className={`${dot} bg-black text-white`}
                data-testid={`tc-inv-create-${orderId}`}
              >
                가상 송장 생성
              </button>
            )}
            {order.tracking_number && del !== 'delivered' && (
              <>
                <button
                  onClick={() => call(`/seller/orders/${orderId}/test-delivery`, { action: 'pickup' })}
                  disabled={busy}
                  className={`${dot} border border-black/20 dark:border-white/20`}
                  data-testid={`tc-del-pickup-${orderId}`}
                >
                  집화완료
                </button>
                <button
                  onClick={() => call(`/seller/orders/${orderId}/test-delivery`, { action: 'in_transit' })}
                  disabled={busy}
                  className={`${dot} border border-black/20 dark:border-white/20`}
                  data-testid={`tc-del-transit-${orderId}`}
                >
                  배송중
                </button>
                <button
                  onClick={() => call(`/seller/orders/${orderId}/test-delivery`, { action: 'delivered' })}
                  disabled={busy}
                  className={`${dot} bg-emerald-600 text-white`}
                  data-testid={`tc-del-done-${orderId}`}
                >
                  배송완료
                </button>
                <button
                  onClick={() => call(`/seller/orders/${orderId}/test-delivery`, { action: 'revert' })}
                  disabled={busy}
                  className={`${dot} border border-gray-400 text-gray-500`}
                  data-testid={`tc-del-revert-${orderId}`}
                >
                  ← 한 단계 되돌리기
                </button>
              </>
            )}
            {order.tracking_number && del !== 'delivered' && (
              <button
                onClick={() => call(`/seller/orders/${orderId}/test-delivery/auto`, { step_sec: 3 })}
                disabled={busy}
                className={`${dot} bg-purple-500 text-white`}
                data-testid={`tc-del-auto-${orderId}`}
              >
                자동 배송 (3s 간격)
              </button>
            )}
          </div>
        </div>
      )}

      {!compact && history.length > 0 && (
        <details className="text-[10px]" data-testid={`tc-history-${orderId}`}>
          <summary className="cursor-pointer text-gray-500">이력 {history.length}건</summary>
          <ul className="pt-1 space-y-0.5">
            {history.slice(-10).map((h) => (
              <li key={h.history_id} className="text-gray-500">
                · {h.event} @ {new Date(h.created_at).toLocaleTimeString('ko-KR')}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
